import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-channel-list>` says about itself.
 *
 * It is a tree with one addition, and the addition is the point. `<caos-tree>` already draws a
 * hierarchy with one row selected; what it has no notion of is a row that is WAITING. For a list of
 * conversations that is the only question the reader is asking — where has something happened since
 * I last looked — so unread is a first-class state here rather than something an author styles on
 * afterwards, and the count is announced rather than drawn as a decoration.
 *
 * Choosing a row reports and marks nothing. `current` is set by whatever opened the conversation,
 * after it opened. A list that marked the row on click is one that lies whenever opening fails.
 *
 * Searching narrows what is already on screen, in this component, over the labels it was given. A
 * search that has to ask something is not this control's.
 */
const docs: ComponentDocs = {
  aliases: ['channel sidebar', 'conversation list', 'chat sidebar', 'rooms list', 'inbox list', 'thread list'],

  answers: [
    'list the conversations somebody can open',
    'show which conversations have unread messages',
    'let somebody find a conversation by name',
    'gather conversations into sections',
  ],

  placements: ['recordPage', 'homePage', 'appPage'],

  inputs: [
    {
      name: 'current',
      attribute: 'current',
      type: 'text',
      description:
        'Which conversation is open, by id. Set it AFTER the conversation opens — this component does ' +
        'not set it on click, so that a failed open does not leave the list claiming otherwise.',
    },
    {
      name: 'searchable',
      attribute: 'searchable',
      type: 'boolean',
      default: false,
      description:
        'Offer a box that narrows the list by name, here, over the labels it was given. Worth offering ' +
        'past about a dozen conversations and not before.',
    },
    {
      name: 'searchPlaceholder',
      attribute: 'search-placeholder',
      type: 'text',
      default: 'Find a conversation',
      description: 'The grey line in the search box, which is also its accessible name.',
    },
    {
      name: 'newLabel',
      attribute: 'new-label',
      type: 'text',
      description:
        'Offer a control at the foot for starting a conversation, with these words on it. Absent ⇒ no ' +
        'control. It reports through the `new` event and creates nothing.',
    },
    {
      name: 'emptyLabel',
      attribute: 'empty-label',
      type: 'text',
      default: 'No conversations yet.',
      description:
        'What is said when there are no conversations at all. A search that matches none says something ' +
        'different and says it automatically — the two are opposite problems and a list that says the ' +
        'same for both reads as one that failed to load.',
    },
  ],

  events: [
    {
      name: 'select',
      carries: '{ id: string }',
      when:
        'Somebody chose a conversation. Nothing has changed yet: opening it, and then setting `current`, ' +
        'is the surface’s.',
    },
    {
      name: 'new',
      carries: 'nothing',
      when: 'Somebody pressed the control named by `new-label`. Nothing has been created.',
    },
  ],

  state: [],
  slots: [],

  tokens: [
    { token: '--caos-color-text', controls: 'A conversation with something waiting in it, and a row under the pointer.' },
    { token: '--caos-color-text-muted', controls: 'A conversation with nothing waiting, and the empty line.' },
    { token: '--caos-color-text-subtle', controls: 'A section heading.' },
    { token: '--caos-color-border', controls: 'The search box’s edge and the new control’s dashed edge.' },
    { token: '--caos-color-surface-2', controls: 'Inside the search box, and behind a row under the pointer.' },
    { token: '--caos-color-accent-fill', controls: 'The open conversation, and the unread count on every other one.' },
    { token: '--caos-color-accent-contrast', controls: 'The text drawn on that fill.' },
    { token: '--caos-font', controls: 'The typeface of the list.' },
    { token: '--caos-text-row', controls: 'The size of a conversation’s name.' },
    { token: '--caos-text-label', controls: 'The size of a section heading and of an unread count.' },
    { token: '--caos-font-weight-label', controls: 'The weight of a section heading, and of a conversation with something waiting.' },
    { token: '--caos-space-1', controls: 'The space under a section heading.' },
    { token: '--caos-space-2', controls: 'The inset of the search box and the new control.' },
    { token: '--caos-space-3', controls: 'The inset of a row from the edge of the list.' },
    { token: '--caos-radius-sm', controls: 'The roundness of the search box and the new control.' },
    { token: '--caos-radius-pill', controls: 'The roundness of an unread count.' },
    { token: '--caos-focus-ring', controls: 'The ring on the search box, each row and the new control.' },
  ],

  parts: [
    { part: 'list', is: 'the scrolling list' },
    { part: 'search', is: 'the box that narrows it' },
    { part: 'section', is: 'a section heading' },
    { part: 'channel', is: 'one conversation' },
    { part: 'unread', is: 'a conversation with something waiting — the same row, additionally named' },
    { part: 'new', is: 'the control that starts a conversation' },
    { part: 'empty', is: 'the line shown when nothing is listed' },
  ],

  methods: [],

  placing:
    'As the column beside a conversation — between the workspace rail, if there is one, and the ' +
    'messages. It is a flex column with its own scroller, so it needs a parent with a HEIGHT, and it ' +
    'draws no edge of its own except the search box’s.',

  whenToUse:
    'A set of named conversations where the reader’s question is which of them is waiting. Without ' +
    'unread there is nothing here a tree does not do better.',

  insteadUse: [
    {
      name: 'Tree',
      when:
        'The rows nest more than one level deep, or the shape that matters is the hierarchy rather ' +
        'than what is waiting. This list gathers into flat sections and no further, on purpose.',
    },
    {
      name: 'NavGadget',
      when: 'The rows are the workspace’s own navigation, which the shell already owns.',
    },
    {
      name: 'DataTable',
      when:
        'The conversations are being administered rather than opened — compared, sorted, selected in ' +
        'bulk. That is a list of records that happen to be conversations.',
    },
  ],

  accessibility:
    'The element is a `role="navigation"` and the rows are a named list, so a reader can find it and ' +
    'skip it. The open conversation carries `aria-current`, which is what says "this one" rather than ' +
    'a colour. An unread count is NAMED — "12 unread" — rather than being read out as a bare number ' +
    'beside a name: the difference between one message and forty is what decides which conversation ' +
    'somebody opens, and a decoration a screen reader skips takes that decision away. Left to the ' +
    'author: `aria-label` on the element saying which set of conversations this is, when a screen has ' +
    'more than one.',

  examples: [
    {
      key: 'sections',
      label: 'Conversations in sections, one of them waiting',
      summary: 'The current conversation is marked, and one other has unread messages.',
      placement: { component: 'caos-channel-list', inputs: { current: 'deploys', 'aria-label': 'Conversations' } },
    },
    {
      key: 'searchable',
      label: 'With a search and a way to start one',
      summary: 'Both are opt-in: a search is noise under about a dozen conversations.',
      placement: {
        component: 'caos-channel-list',
        inputs: {
          searchable: true,
          'search-placeholder': 'Find a conversation',
          'new-label': '+ New conversation',
          'aria-label': 'Conversations',
        },
      },
    },
  ],
};

export default docs;
